import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { Transaction } from '../../types';

interface MonthlyTrendChartProps {
  transactions: Transaction[];
}

export default function MonthlyTrendChart({ transactions }: MonthlyTrendChartProps) {
  // Process monthly data
  const monthlyData = useMemo(() => {
    const dataMap = new Map<string, { income: number; expenses: number }>();
    
    transactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
      
      const current = dataMap.get(monthKey) || { income: 0, expenses: 0 };
      
      if (transaction.type === 'income') {
        current.income += transaction.amount;
      } else {
        current.expenses += Math.abs(transaction.amount);
      }
      
      dataMap.set(monthKey, current);
    });
    
    // Get last 6 months
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    const result = [];
    
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (now.getMonth() - i + 12) % 12;
      const monthName = months[monthIndex];
      
      const values = dataMap.get(monthName) || { income: 0, expenses: 0 };
      const net = values.income - values.expenses;
      
      result.push({
        month: monthName,
        income: values.income,
        expenses: values.expenses,
        net: net,
        color: net >= 0 ? '#10b981' : '#ef4444'
      });
    }
    
    return result;
  }, [transactions]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const income = payload.find((p: any) => p.dataKey === 'income')?.value || 0;
      const expenses = payload.find((p: any) => p.dataKey === 'expenses')?.value || 0;
      const net = income - expenses;
      
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-gray-300 mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Income:</span>
              <span className="font-medium text-green-400">${income.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Expenses:</span>
              <span className="font-medium text-red-400">${expenses.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-gray-700">
              <span className="text-sm text-gray-400">Net:</span>
              <span className={`font-medium ${net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                ${net.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate totals
  const totals = useMemo(() => {
    return monthlyData.reduce((acc, month) => ({
      income: acc.income + month.income,
      expenses: acc.expenses + month.expenses,
    }), { income: 0, expenses: 0 });
  }, [monthlyData]);

  const netTotal = totals.income - totals.expenses;
  const growthRate = monthlyData.length >= 2 
    ? ((monthlyData[monthlyData.length - 1].net - monthlyData[monthlyData.length - 2].net) / 
       Math.abs(monthlyData[monthlyData.length - 2].net || 1)) * 100 
    : 0;

  return (
    <div className="chart-container">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Monthly Trends</h3>
          <p className="text-sm text-gray-400">6-month performance overview</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className={`flex items-center px-3 py-1 rounded-lg text-sm ${netTotal >= 0 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
            {growthRate >= 0 ? (
              <TrendingUp className="w-4 h-4 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 mr-1" />
            )}
            {growthRate >= 0 ? '+' : ''}{growthRate.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-6">
        <div className="bg-gray-700/30 rounded-lg p-3">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 text-blue-400 mr-2" />
            <span className="text-sm text-gray-400">Avg Monthly Income</span>
          </div>
          <p className="text-lg font-bold text-green-400 mt-1">
            ${monthlyData.length > 0 ? (totals.income / monthlyData.length).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'}
          </p>
        </div>
        
        <div className="bg-gray-700/30 rounded-lg p-3">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 text-blue-400 mr-2" />
            <span className="text-sm text-gray-400">Avg Monthly Expenses</span>
          </div>
          <p className="text-lg font-bold text-red-400 mt-1">
            ${monthlyData.length > 0 ? (totals.expenses / monthlyData.length).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'}
          </p>
        </div>
        
        <div className="bg-gray-700/30 rounded-lg p-3">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 text-blue-400 mr-2" />
            <span className="text-sm text-gray-400">Avg Monthly Net</span>
          </div>
          <p className={`text-lg font-bold mt-1 ${netTotal >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            ${monthlyData.length > 0 ? (netTotal / monthlyData.length).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'}
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-48 sm:h-56 lg:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis 
              dataKey="month" 
              stroke="#9CA3AF"
              fontSize={12}
              tick={{ fill: '#9CA3AF' }}
            />
            <YAxis 
              stroke="#9CA3AF"
              fontSize={12}
              tick={{ fill: '#9CA3AF' }}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="income" 
              name="Income"
              radius={[4, 4, 0, 0]}
              fill="#10b981"
              fillOpacity={0.8}
            />
            <Bar 
              dataKey="expenses" 
              name="Expenses"
              radius={[4, 4, 0, 0]}
              fill="#ef4444"
              fillOpacity={0.8}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-4 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
          <span className="text-gray-400">Income</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
          <span className="text-gray-400">Expenses</span>
        </div>
      </div>
    </div>
  );
}